import { useEffect, useState } from "react";
import { API_BASE } from "../api";
import NewsSheet, { Edition } from "./NewsSheet";
import StonkyPortrait from "./StonkyPortrait";

// The public reading room — no login. Shows the firm's overview and today's
// Bull Sheet, and points prospects at agent@stonky.ai. Read-only, safe to share:
// it fetches only /api/public/bullsheet (news + editorial, nothing account-specific).
type Cta = { headline: string; body: string; email: string };

export default function PublicPortal() {
  const [ed, setEd] = useState<Edition | null>(null);
  const [cta, setCta] = useState<Cta | null>(null);
  const [loaded, setLoaded] = useState(false);

  // /share/<edition id> opens one specific issue (what a shared link named);
  // bare /share is the latest.
  const editionId = (window.location.pathname.match(/^\/share\/(\d+)/) || [])[1];
  useEffect(() => {
    fetch(`${API_BASE}/api/public/bullsheet${editionId ? `?edition=${editionId}` : ""}`)
      .then((r) => r.json())
      .then((d) => { setEd(d.edition ?? null); setCta(d.cta ?? null); })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const email = cta?.email ?? "agent@stonky.ai";
  const [copied, setCopied] = useState(false);
  const shareUrl = ed?.issue?.share_url;
  const copyShare = async () => {
    if (!shareUrl) return;
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { window.prompt("Copy this link:", shareUrl); }
  };
  useEffect(() => {
    if (ed?.issue) document.title = `The Bull Sheet — ${ed.issue.label}: ${ed.lead?.headline ?? ""}`;
  }, [ed]);

  return (
    <div className="min-h-screen scroll-smooth">
      <header className="sticky top-0 z-20 border-b border-edge bg-ink/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <span className="font-black tracking-tight text-lg uppercase">Stonky</span>
          <span className="text-[10px] font-mono tracking-[0.18em] text-slate-500 uppercase hidden sm:inline">
            Public reading room
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <a href="#bullsheet" className="btn text-sm">Read the Bull Sheet</a>
            <a href={`mailto:${email}?subject=My%20own%20AI%20trading%20firm`}
               className="btn btn-primary text-sm">Get your own firm</a>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 space-y-6">
        <StonkyPortrait />

        <div className="rounded-lg border border-gold/30 bg-gold/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <div className="text-base font-bold text-slate-100">{cta?.headline ?? "Run your own AI trading firm."}</div>
            <p className="text-sm text-slate-400 mt-0.5">
              {cta?.body ?? "A governed board and desk of specialized AI agents, trading under a risk code no agent can override."}
            </p>
          </div>
          <a href={`mailto:${email}?subject=My%20own%20AI%20trading%20firm`}
             className="btn btn-primary shrink-0">Email {email}</a>
        </div>

        <div id="bullsheet" className="scroll-mt-20">
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <h2 className="text-lg font-bold mb-0">The Bull Sheet <span className="text-slate-500 text-sm font-normal">— {ed?.issue ? `${ed.issue.label} · ${ed.date}` : "Stonky’s morning wire"}</span></h2>
            {shareUrl && <button className="btn text-sm" onClick={copyShare}>{copied ? "Link copied ✓" : "Share this edition"}</button>}
          </div>
          {loaded && !ed ? (
            <div className="card text-slate-500 text-sm">Today’s edition hasn’t been pressed yet — check back this morning.</div>
          ) : (
            <NewsSheet ed={ed} />
          )}
        </div>

        <footer className="text-center text-xs text-slate-600 py-6 border-t border-edge">
          Stonky — a governed AI trading firm. This page is a read-only preview; written from the firm’s own feeds,
          not investment advice. Want a firm of your own? <a className="text-accent" href={`mailto:${email}`}>{email}</a>
        </footer>
      </main>
    </div>
  );
}
